// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
  Box,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { FaExchangeAlt } from "react-icons/fa"
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

import { type ApiError, type ReportJobPublic ,type ReportJobUpdate, ReportsService, type ReportJobType, type ReportJobSchedule } from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Field } from "../ui/field"
import {
  reportTypeOptions,
  scheduleOptions,
} from "@/types/report"

interface EditReportJobPublicProps {
  reportJob: ReportJobPublic
}

const EditReportJob = ({ reportJob }: EditReportJobPublicProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [selectedType, setSelectedType] = useState<ReportJobType>("briefing")
  const [selectedSchedule, setSelectedSchedule] = useState<ReportJobSchedule>("once")
  const [startTime, setStartTime] = useState("08:00")
  const [endDate, setEndDate] = useState(() => {
    const today = new Date()
    return today.toISOString().split("T")[0] // "YYYY-MM-DD"
  })

  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const { data: feedData } = useQuery({
    queryKey: ["feedMetadata"],
    queryFn: () => ReportsService.readFeedMetadata(),
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ReportJobUpdate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      feed_ids: reportJob.feed_ids,
      report_job_name: reportJob.report_job_name,
      description: reportJob.description,
      report_type: reportJob.report_type,
      schedule: reportJob.schedule,
    },
  })

  const selectedFeedIds = watch("feed_ids") || []

  const mutation = useMutation({
    mutationFn: ({ jobId, payload }: { jobId: string; payload: ReportJobUpdate }) =>
      ReportsService.updateReportJob({ jobId, requestBody: payload }),
  
    onSuccess: () => {
      showSuccessToast("리포트 작업이 수정되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["reportJobs"] })
      reset()
      setIsOpen(false)
      setHasSubmitted(false)
    },
  
    onError: (err: ApiError) => {
      handleError(err)
      setHasSubmitted(false)
    },
  })

  const toggleFeedSelection = (feedId: string) => {
    const current = watch("feed_ids") || []
    const updated = current.includes(feedId)
      ? current.filter((jobId) => jobId !== feedId)
      : [...current, feedId]
    setValue("feed_ids", updated)
  }

  const onSubmit: SubmitHandler<ReportJobUpdate> = (formData) => {
    if (hasSubmitted) return
    setHasSubmitted(true)
  
    setValue("report_type", selectedType)
    setValue("schedule", selectedSchedule) // optional field
    const finalData: ReportJobUpdate = {
      ...formData,
      feed_ids: watch("feed_ids"),
      report_type: watch("report_type"),
      schedule: watch("schedule"),
    }
  
    mutation.mutate({
      jobId: reportJob.id,
      payload: formData,
    })
  
  }

  const filteredScheduleOptions = scheduleOptions.filter(
    (option) => option.value !== "stop"
  )

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (open) {
          reset({
            feed_ids: reportJob.feed_ids,
            report_job_name: reportJob.report_job_name,
            description: reportJob.description,
            report_type: reportJob.report_type,
            schedule: reportJob.schedule,
          })
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <FaExchangeAlt fontSize="16px" />
          리포트 생성 작업 수정
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>리포트 생성 작업 수정</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Text mb={4}>리포트 작업의 세부 정보를 수정하세요.</Text>
            <VStack gap={4} align="start">
              <Field label="리포트 이름" required>
                <Input {...register("report_job_name", { required: "필수 입력 항목입니다." })} />
              </Field>

              <Field label="리포트 설명">
                <Input {...register("description")} />
              </Field>

              <Field label="피드 선택" required />
              <Box maxH="200px" overflowY="auto" w="100%" pr={2}>
                <VStack align="start">
                  {feedData?.data?.map((feed) => (
                    <Checkbox
                      key={feed.id}
                      checked={selectedFeedIds.includes(feed.id)}
                      onCheckedChange={() => toggleFeedSelection(feed.id)}
                      mb={2}
                    >
                      <Box>
                        <Text fontWeight="medium">{feed.feed_name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {feed.feed_description}
                        </Text>
                      </Box>
                    </Checkbox>
                  ))}
                </VStack>
              </Box>

              <Field label="리포트 유형" required />
              <Select
                name="report_type"
                options={reportTypeOptions}
                value={selectedType}
                onChange={(val) => {
                  setSelectedType(val as ReportJobType)
                  setValue("report_type", val as ReportJobType)
                }}
                isRequired
                portalled={false}
              />

              <Field label="생성 주기" required />
              <Select
                name="schedule"
                options={filteredScheduleOptions}
                value={selectedSchedule}
                onChange={(val) => {
                  setSelectedSchedule(val as ReportJobSchedule)
                  setValue("schedule", val as ReportJobSchedule)
                }}
                isRequired
                portalled={false}
              />

              {selectedSchedule !== "once" && (
                <>
                  <Field label="생성 시간" required>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Field>

                  <Field label="생성 기간 종료일" required>
                    <Input
                      type="date"
                      value={endDate}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button variant="subtle" disabled={isSubmitting}>취소</Button>
              </DialogActionTrigger>
              <Button
                type="submit"
                variant="solid"
                disabled={!isValid || isSubmitting || hasSubmitted}
                loading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}


export default EditReportJob
